import { Icon, IconProps } from '@/editor/components/ui/Icon';
import { Toolbar } from '@/editor/components/ui/Toolbar';
import { Editor } from '@tiptap/react';
import { useEffect, useReducer } from 'react';
import { insertPromptTable } from './prompt-table';

type ToolbarItem =
  | 'divider'
  | {
      icon: IconProps['name'];
      tooltip: string;
      shortcut?: string[];
      run: (editor: Editor) => void;
      isActive?: (editor: Editor) => boolean;
    };

const heading = (level: 1 | 2 | 3): ToolbarItem => ({
  icon: `Heading${level}`,
  tooltip: `Heading ${level}`,
  shortcut: ['Mod', 'Alt', String(level)],
  run: (editor) => editor.chain().focus().toggleHeading({ level }).run(),
  isActive: (editor) => editor.isActive('heading', { level }),
});

const ITEMS: ToolbarItem[] = [
  heading(1),
  heading(2),
  heading(3),
  'divider',
  {
    icon: 'Bold',
    tooltip: 'Bold',
    shortcut: ['Mod', 'B'],
    run: (editor) => editor.chain().focus().toggleBold().run(),
    isActive: (editor) => editor.isActive('bold'),
  },
  {
    icon: 'Italic',
    tooltip: 'Italic',
    shortcut: ['Mod', 'I'],
    run: (editor) => editor.chain().focus().toggleItalic().run(),
    isActive: (editor) => editor.isActive('italic'),
  },
  {
    icon: 'Underline',
    tooltip: 'Underline',
    shortcut: ['Mod', 'U'],
    run: (editor) => editor.chain().focus().toggleUnderline().run(),
    isActive: (editor) => editor.isActive('underline'),
  },
  {
    icon: 'Code',
    tooltip: 'Code',
    shortcut: ['Mod', 'E'],
    run: (editor) => editor.chain().focus().toggleCode().run(),
    isActive: (editor) => editor.isActive('code'),
  },
  'divider',
  {
    icon: 'List',
    tooltip: 'Bullet list',
    shortcut: ['Mod', 'Shift', '8'],
    run: (editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor) => editor.isActive('bulletList'),
  },
  {
    icon: 'ListOrdered',
    tooltip: 'Numbered list',
    shortcut: ['Mod', 'Shift', '7'],
    run: (editor) => editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor) => editor.isActive('orderedList'),
  },
  {
    icon: 'Quote',
    tooltip: 'Quote',
    shortcut: ['Mod', 'Shift', 'B'],
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
    isActive: (editor) => editor.isActive('blockquote'),
  },
  'divider',
  {
    icon: 'Table',
    tooltip: 'Insert table',
    run: (editor) => insertPromptTable(editor),
    isActive: (editor) => editor.isActive('table'),
  },
];

// Shown next to the table button while the cursor is in a table
const TABLE_ITEMS: ToolbarItem[] = [
  {
    icon: 'BetweenHorizontalEnd',
    tooltip: 'Add row below',
    run: (editor) => editor.chain().focus().addRowAfter().run(),
  },
  {
    icon: 'BetweenVerticalEnd',
    tooltip: 'Add column right',
    run: (editor) => editor.chain().focus().addColumnAfter().run(),
  },
  {
    icon: 'TableRowsSplit',
    tooltip: 'Delete row',
    run: (editor) => editor.chain().focus().deleteRow().run(),
  },
  {
    icon: 'TableColumnsSplit',
    tooltip: 'Delete column',
    run: (editor) => editor.chain().focus().deleteColumn().run(),
  },
  {
    icon: 'Trash2',
    tooltip: 'Delete table',
    run: (editor) => editor.chain().focus().deleteTable().run(),
  },
];

export default function PromptToolbar({ editor }: { editor: Editor | null }) {
  // Re-render on every transaction so active states follow the cursor
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    if (!editor) return;
    editor.on('transaction', rerender);
    return () => {
      editor.off('transaction', rerender);
    };
  }, [editor]);

  if (!editor) return null;

  const items = editor.isActive('table') ? [...ITEMS, ...TABLE_ITEMS] : ITEMS;

  return (
    <Toolbar.Wrapper className="shadow-none border-0 p-0 flex-wrap">
      {items.map((item, index) =>
        item === 'divider' ? (
          <Toolbar.Divider key={`divider-${index}`} />
        ) : (
          <Toolbar.Button
            key={item.tooltip}
            type="button"
            tooltip={item.tooltip}
            tooltipShortcut={item.shortcut}
            aria-label={item.tooltip}
            active={item.isActive?.(editor)}
            // Keep the editor selection when clicking the button
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              item.run(editor);
            }}
          >
            <Icon name={item.icon} />
          </Toolbar.Button>
        )
      )}
    </Toolbar.Wrapper>
  );
}
